const canUseDom =
  typeof window !== "undefined" &&
  typeof document !== "undefined" &&
  typeof HTMLElement !== "undefined" &&
  typeof customElements !== "undefined";

if (canUseDom) {
  class NewsletterPopup extends HTMLElement {
    constructor() {
      super();
      this.popupId = this.id || "newsletter-popup-default";
      this.dismissedStorageKey = `newsletter_popup_dismissed_${this.popupId}`;
      this.localStorageKey = `newsletter_popup_subscribed_${this.popupId}`;
      // Once per visitor: a dismissal is remembered on the device for this
      // many days (subscribers never see the popup again at all).
      this.dismissDays = 30;
      // Breathing room (ms) after the visitor closes Shopify's cookie-consent
      // banner before the popup opens — opening the instant consent clears reads
      // as aggressive. Only applies when we actually deferred for the banner.
      this.consentGraceMs = 10000;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      /** @type {HTMLElement | null} */
      this.modal = this.querySelector(".newsletter-popup-modal");
      /** @type {HTMLElement | null} */
      this.overlay = this.querySelector(".newsletter-popup-overlay");
      /** @type {HTMLButtonElement | null} */
      this.closeBtn = this.querySelector(".newsletter-popup-close");
      /** @type {HTMLFormElement | null} */
      this.form = this.querySelector("form");
      /** @type {HTMLElement | null} */
      this.inputGroup = this.querySelector(".newsletter-popup-input-group");
      /** @type {HTMLElement | null} */
      this.successMessage = this.querySelector(
        "[data-ajax-newsletter-success]",
      );
      /** @type {HTMLElement | null} */
      this.errorMessage = this.querySelector("[data-ajax-newsletter-error]");
      /** @type {HTMLElement | null} */
      this.successCode = this.querySelector("[data-ajax-newsletter-code]");
      // The submit button's busy state and clearing the email input are owned by
      // newsletter-ajax.js — this element only drives the popup's own states.
      this.popupId = this.overlay?.dataset.popupId || this.popupId;

      if (!this.modal || !this.overlay || !this.closeBtn) return;

      this.setupEventListeners();
      this.checkStorageAndShow();
    }

    setupEventListeners() {
      // Close button
      this.closeBtn?.addEventListener("click", () => this.dismissPopup());

      // Overlay click
      this.overlay?.addEventListener("click", () => this.dismissPopup());

      // Prevent modal click from closing
      this.modal?.addEventListener("click", (e) => e.stopPropagation());

      // Form submission is handled by the shared, hCaptcha-aware
      // newsletter-ajax.js (bound via [data-ajax-newsletter], loaded globally in
      // theme.liquid). We must NOT add a competing submit handler here: this
      // element upgrades before newsletter-ajax.js binds, so a capture-phase
      // listener calling stopImmediatePropagation() hijacked every submit and
      // POSTed without an h-captcha-response token — Shopify then bounced it to
      // /challenge and the popup always showed "Something went wrong".
      // Same failure the cart-drawer banner hit (see snippets/cart-drawer.liquid).
      //
      // We only react to its result events to apply the popup's bespoke states.
      if (this.form) {
        this.form.addEventListener("newsletter:success", () =>
          this.onSubscribed(),
        );
        this.form.addEventListener("newsletter:error", () =>
          this.onSubscribeError(),
        );
      }

      // Keyboard - ESC to close
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.modal?.classList.contains("active")) {
          this.dismissPopup();
        }
      });
    }

    checkStorageAndShow() {
      const isSubscribed = localStorage.getItem(this.localStorageKey);

      // Once per visitor: dismissed within the last `dismissDays` → stay hidden
      let isDismissed = false;
      try {
        const dismissedAt = parseInt(
          localStorage.getItem(this.dismissedStorageKey) || "0",
          10,
        );
        isDismissed =
          dismissedAt > 0 &&
          Date.now() - dismissedAt < this.dismissDays * 86400000;
      } catch (_) {}

      if (isSubscribed || isDismissed) {
        // Not eligible — release priority so lower-priority popups
        // (sections/popup.liquid) may show.
        window.__newsletterPopupActive = false;
        return;
      }

      // The newsletter is the highest-priority popup: claim priority now, even
      // while waiting out the delay, so popup-modal popups stay suppressed until
      // this one has shown and been dismissed.
      window.__newsletterPopupActive = true;

      // Delay (seconds) before the popup opens. A missing, blank, zero, or
      // otherwise invalid delay must NOT open the popup on the first frame —
      // that is the "popup flashes in the instant the page loads" bug (a
      // cleared/0 "Show popup after (seconds)" setting renders data-delay=""
      // or "0"). Fall back to the schema default (2s) so it always waits.
      const parsed = parseInt(this.dataset.delay, 10);
      const delay = Number.isFinite(parsed) && parsed > 0 ? parsed : 2;

      setTimeout(() => this.showWhenConsentClear(), delay * 1000);
    }

    // Shopify's Customer Privacy consent UI — the cookie banner
    // (#shopify-pc__banner) and the "Manage preferences" dialog
    // (#shopify-pc__prefs) — must never be covered by the newsletter popup. Both
    // are injected at <body> level by Shopify's consent script, so we query the
    // document, not this element. Note Shopify does NOT remove the banner on a
    // choice; it sets style="display:none", so presence alone is not "active" —
    // test the computed display/visibility. The banner is position:fixed, so
    // offsetParent is unreliable here; getComputedStyle is the dependable check.
    isConsentUiVisible() {
      return ["shopify-pc__banner", "shopify-pc__prefs"].some((id) => {
        const el = document.getElementById(id);
        if (!el) return false;
        const cs = getComputedStyle(el);
        return cs.display !== "none" && cs.visibility !== "hidden";
      });
    }

    // Open the popup — but only once the cookie-consent UI is out of the way.
    // Priority is already claimed (see checkStorageAndShow), so the exit-intent
    // popups in sections/popup.liquid stay suppressed while we wait; we're only
    // deferring the visual open. If the banner is up we hold until the visitor
    // accepts/declines/saves, signalled by Shopify's `visitorConsentCollected`
    // event, with a DOM observer as a fallback (covers the banner→prefs handoff,
    // where the banner hides before the prefs dialog closes, and the case where
    // Shopify injects the banner a beat after this fires).
    showWhenConsentClear() {
      if (!this.isConsentUiVisible()) {
        this.showPopup();
        return;
      }

      const onResolved = () => {
        // Banner may hide to open the prefs dialog — wait for a clear screen.
        if (this.isConsentUiVisible()) return;
        cleanup();
        // Give the visitor a beat after dealing with the cookie banner instead
        // of pouncing the instant consent closes.
        this._consentGraceTimer = setTimeout(
          () => this.showPopup(),
          this.consentGraceMs,
        );
      };
      const cleanup = () => {
        this._consentObserver?.disconnect();
        this._consentObserver = null;
        document.removeEventListener("visitorConsentCollected", onResolved);
      };

      document.addEventListener("visitorConsentCollected", onResolved);
      this._consentObserver = new MutationObserver(onResolved);
      this._consentObserver.observe(document.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    }

    showPopup() {
      // Small delay to ensure DOM is ready for animation
      requestAnimationFrame(() => {
        this.overlay?.classList.add("active");
        this.modal?.classList.add("active");
        document.body.classList.add("newsletter-popup-active");
      });
    }

    dismissPopup() {
      // Once per visitor: remember the dismissal on the device
      try {
        localStorage.setItem(this.dismissedStorageKey, Date.now().toString());
      } catch (_) {}

      // Release priority so a lower-priority popup (e.g. exit-intent) may arm
      // once this one is closed.
      window.__newsletterPopupActive = false;

      this.overlay?.classList.remove("active");
      this.modal?.classList.remove("active");
      document.body.classList.remove("newsletter-popup-active");

      this.modal?.setAttribute("aria-modal", "false");
    }

    onSubscribed() {
      localStorage.setItem(this.localStorageKey, "true");
      this.showResultState("success");

      // Auto-close 7 s after a successful signup (close button still works).
      // Kept longer than the abandon popup so the discount code stays readable.
      clearTimeout(this._successCloseTimer);
      this._successCloseTimer = setTimeout(() => this.dismissPopup(), 7000);
    }

    onSubscribeError() {
      this.showResultState("error");
    }

    /** @param {"success" | "error"} type */
    showResultState(type) {
      if (type === "success") {
        if (this.inputGroup) this.inputGroup.setAttribute("hidden", "");
        this.successMessage?.removeAttribute("hidden");
        this.successCode?.removeAttribute("hidden");
        this.errorMessage?.setAttribute("hidden", "");
        return;
      }

      this.inputGroup?.removeAttribute("hidden");
      this.successCode?.setAttribute("hidden", "");
      this.successMessage?.setAttribute("hidden", "");
      this.errorMessage?.removeAttribute("hidden");
    }
  }

  // Register custom element
  if (!customElements.get("newsletter-popup")) {
    customElements.define("newsletter-popup", NewsletterPopup);
  }

  // Initialize on page load if DOM is already ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      // Auto-initialize all newsletter-popup elements
    });
  }
}
